import { useState, useEffect, useRef } from 'react'
import { Plus, Pencil, Trash2, Box, Upload, ExternalLink, Loader2, CheckCircle2 } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { Modal } from '@/components/ui/Modal'
import { Toggle } from '@/components/ui/Toggle'
import { ImageUpload } from '@/components/ui/ImageUpload'
import { fetchAdminModels, createModel, updateModel, deleteModel, uploadGlbAsset, getAssetUrl } from '@/lib/api'
import type { Model } from '@/types'

const EMPTY: Partial<Model> = {
  name: '', description: '', base_price: 0, sqft: 0,
  sleeps: 1, bathrooms: 1, has_loft: 0, weight_limit: null,
  image_url: null, glb_url: null, best_for: null, sort_order: 99, is_active: 1,
}

// ── GLB uploader widget ───────────────────────────────────────────────────────

function GlbUpload({
  value,
  onChange,
}: {
  value: string | null
  onChange: (url: string | null) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.glb')) {
      setError('Only .glb files are accepted')
      return
    }
    setUploading(true)
    setError(null)
    try {
      const result = await uploadGlbAsset(file)
      onChange(result.url)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  const filename = value?.split('/').pop() ?? null

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">
        3D Model (GLB)
      </label>

      {value ? (
        // GLB assigned state
        <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-green-800">GLB assigned</p>
            <p className="text-xs text-green-600 truncate font-mono mt-0.5">{filename}</p>
            <div className="flex items-center gap-3 mt-2">
              <a
                href={getAssetUrl(value)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-green-700 hover:underline"
              >
                <ExternalLink className="h-3 w-3" /> Preview
              </a>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-xs text-green-700 hover:underline"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-xs text-red-500 hover:underline"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Empty state — drop zone + pipeline link
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => !uploading && inputRef.current?.click()}
          className="border-2 border-dashed border-slate-200 hover:border-brand-400 rounded-lg p-5 flex flex-col items-center gap-2 cursor-pointer transition-colors text-center"
        >
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 text-brand-400 animate-spin" />
              <span className="text-sm text-slate-500">Uploading…</span>
            </>
          ) : (
            <>
              <Box className="h-6 w-6 text-slate-300" />
              <div>
                <p className="text-sm text-slate-500">Drop a <span className="font-medium">.glb</span> file here or click to browse</p>
                <p className="text-xs text-slate-400 mt-0.5">Upload a finished GLB directly, or</p>
              </div>
              <NavLink
                to="/admin/pipeline"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-xs text-brand-600 font-medium hover:underline"
              >
                <Upload className="h-3 w-3" />
                Generate from CAD/DXF via Pipeline →
              </NavLink>
            </>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept=".glb"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
      />
    </div>
  )
}

// ── Model form ────────────────────────────────────────────────────────────────

function ModelForm({
  init, onSave, onClose,
}: { init: Partial<Model>; onSave: (m: Model) => void; onClose: () => void }) {
  const [form, setForm] = useState<Partial<Model>>(init)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isNew = !form.id

  function set<K extends keyof Model>(key: K, val: Model[K]) {
    setForm((f) => ({ ...f, [key]: val }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const saved = isNew
        ? await createModel(form)
        : await updateModel(form.id!, form)
      onSave(saved)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const input = (key: keyof Model, label: string, type = 'text') => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type={type}
        value={String(form[key] ?? '')}
        onChange={(e) => set(key, (type === 'number' ? Number(e.target.value) : e.target.value) as Model[typeof key])}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
      />
    </div>
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {input('name', 'Name')}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
        <textarea
          rows={3}
          value={form.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        {input('base_price', 'Base Price ($)', 'number')}
        {input('sqft', 'Sq Ft', 'number')}
        {input('sleeps', 'Sleeps', 'number')}
        {input('bathrooms', 'Bathrooms', 'number')}
        {input('weight_limit', 'Weight Limit (lbs)', 'number')}
        {input('sort_order', 'Sort Order', 'number')}
      </div>
      {input('best_for', 'Best For')}

      {/* ── Media ─────────────────────────────────────────────────────────────── */}
      <div className="border-t border-slate-100 pt-4 space-y-4">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Media</p>

        <ImageUpload
          label="Thumbnail Image (shown on model selection cards)"
          value={form.image_url ?? null}
          onChange={(url) => set('image_url', url)}
        />

        <GlbUpload
          value={form.glb_url ?? null}
          onChange={(url) => set('glb_url', url as Model['glb_url'])}
        />
      </div>

      <div className="flex items-center gap-4">
        <Toggle
          checked={!!form.has_loft}
          onChange={(v) => set('has_loft', v ? 1 : 0)}
          label="Has Loft"
        />
        {!isNew && (
          <Toggle
            checked={!!form.is_active}
            onChange={(v) => set('is_active', v ? 1 : 0)}
            label="Active"
          />
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : isNew ? 'Create Model' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminModels() {
  const [models, setModels] = useState<Model[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'add' | Model | null>(null)

  useEffect(() => {
    fetchAdminModels()
      .then(setModels)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  function handleSaved(m: Model) {
    setModels((prev) => {
      const idx = prev.findIndex((x) => x.id === m.id)
      return idx >= 0 ? prev.map((x) => x.id === m.id ? m : x) : [...prev, m]
    })
    setModal(null)
  }

  async function handleDelete(m: Model) {
    if (!confirm(`Delete "${m.name}"? This cannot be undone.`)) return
    try {
      await deleteModel(m.id)
      setModels((prev) => prev.filter((x) => x.id !== m.id))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Models"
        subtitle="Manage base models, thumbnails, and 3D assets"
        action={
          <button
            onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Model
          </button>
        }
      />

      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Base Price</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden md:table-cell">Sq Ft</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden lg:table-cell">3D Model</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      {m.image_url ? (
                        <img
                          src={getAssetUrl(m.image_url)}
                          alt=""
                          className="h-8 w-10 object-cover rounded shrink-0 bg-slate-100"
                        />
                      ) : (
                        <div className="h-8 w-10 bg-slate-100 rounded shrink-0 flex items-center justify-center">
                          <span className="text-xs text-slate-400 font-bold">{m.name[0]}</span>
                        </div>
                      )}
                      <span className="font-medium text-slate-800">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 hidden sm:table-cell">${m.base_price.toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-600 hidden md:table-cell">{m.sqft} sqft</td>
                  <td className="px-5 py-3 hidden lg:table-cell">
                    {m.glb_url ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                        <Box className="h-3 w-3" /> GLB ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        <Box className="h-3 w-3" /> Placeholder
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {m.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setModal(m)} className="text-slate-400 hover:text-brand-600 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(m)} className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          title={modal === 'add' ? 'Add Model' : `Edit — ${(modal as Model).name}`}
          onClose={() => setModal(null)}
          size="lg"
        >
          <ModelForm
            init={modal === 'add' ? EMPTY : (modal as Model)}
            onSave={handleSaved}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
    </AdminLayout>
  )
}
